import { PREFERRED_ACTIVITY_OPTIONS } from "@/components/assessment/assessmentData";

export const COMPANION_CALENDAR_KEY = "forr:companion-calendar:v1";

export const CALENDAR_EVENT_TYPES = [
  { key: "workout", label: "Workout or training program" },
  { key: "sport", label: "Sport practice or game" },
  { key: "competition", label: "Competition or performance" },
  { key: "recreation", label: "Recreation, play, or skill activity" },
  { key: "coaching", label: "1:1 coaching" },
  { key: "group-class", label: "Group class or team session" },
  { key: "physiotherapy", label: "Physiotherapy or rehabilitation" },
  { key: "counselling", label: "Counselling or therapy" },
  { key: "work-demand", label: "Work, school, or performance demand" },
  { key: "caregiving", label: "Caregiving or family demand" },
  { key: "travel", label: "Travel or schedule transition" },
  { key: "recovery", label: "Recovery, mobility, or bedtime routine" },
  { key: "custom", label: "Another program or demand" },
];

export const WORKOUT_MODALITIES = [
  { key: "strength", label: "Strength / weights" },
  { key: "cardio", label: "Cardio / steady movement" },
  { key: "interval", label: "Intervals / high output" },
  { key: "mixed", label: "Mixed exercise session" },
  { key: "technical-skill", label: "Technical or skill practice" },
  { key: "sport-practice", label: "Sport practice" },
  { key: "competition", label: "Game, competition, or performance" },
  { key: "mobility", label: "Mobility / stretching / somatic work" },
  { key: "rehabilitation", label: "Physiotherapy / rehabilitation" },
  { key: "conversation", label: "Counselling / conversation" },
  { key: "work", label: "Work or task demand" },
  { key: "travel", label: "Travel / transition" },
  { key: "custom", label: "Custom format" },
];

export const USUAL_EFFORT_OPTIONS = [
  { key: "easy", label: "Easy relative to me" },
  { key: "moderate", label: "Moderate relative to me" },
  { key: "challenging", label: "Challenging relative to me" },
  { key: "very-challenging", label: "Very challenging relative to me" },
  { key: "variable", label: "Variable or unpredictable" },
];

export const ACTIVITY_FAMILIARITY_OPTIONS = [
  { key: "new", label: "New or unfamiliar" },
  { key: "some", label: "Some experience" },
  { key: "familiar", label: "Familiar and technically comfortable" },
  { key: "highly-familiar", label: "Highly familiar / automatic skill" },
];

export const SOCIAL_FORMAT_OPTIONS = [
  { key: "solo", label: "Solo" },
  { key: "with-support", label: "With a friend, partner, or family member" },
  { key: "one-to-one-coaching", label: "1:1 coaching" },
  { key: "small-group", label: "Small group" },
  { key: "group-class", label: "Group class" },
  { key: "team", label: "Team" },
  { key: "large-group", label: "Larger group or public setting" },
];

export const COMPETITION_CONTEXT_OPTIONS = [
  { key: "none", label: "Non-competitive / no evaluation" },
  { key: "practice", label: "Practice with some evaluation" },
  { key: "competitive", label: "Competitive or performance-based" },
  { key: "variable", label: "Varies by session" },
];

export const MODIFICATION_OPTIONS = [
  { key: "yes", label: "Yes, the session can be modified" },
  { key: "limited", label: "Only some parts can be modified" },
  { key: "no", label: "No, the demand is mostly fixed" },
];

export const RECOVERY_COST_OPTIONS = [
  { key: "low", label: "Low recovery cost" },
  { key: "moderate", label: "Moderate recovery cost" },
  { key: "high", label: "High recovery cost" },
  { key: "variable", label: "Variable or unpredictable recovery cost" },
];

export const REMINDER_OPTIONS = [
  { value: 0, label: "At start time" },
  { value: 10, label: "10 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
];

const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const pad = (value) => String(value).padStart(2, "0");
const localDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const activityLabel = (key, fallback = "Scheduled activity") =>
  PREFERRED_ACTIVITY_OPTIONS.find((option) => option.key === key)?.label || fallback;

const parseEventDate = (event) => {
  if (!event?.date) return null;
  const [year, month, day] = String(event.date).split("-").map(Number);
  const [hour, minute] = String(event.time || "12:00").split(":").map(Number);
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeProgramEvent = (event = {}) => ({
  id:
    event.id ||
    `calendar-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  source: event.source || "user",
  title: String(event.title || "Scheduled activity").trim(),
  date: event.date || localDateKey(new Date()),
  time: event.time || "18:00",
  durationMinutes: Math.max(5, Number(event.durationMinutes) || 30),
  category: event.category || "workout",
  modality: event.modality || "mixed",
  usualEffort: event.usualEffort || "moderate",
  familiarity: event.familiarity || "familiar",
  socialFormat: event.socialFormat || "solo",
  competitionContext:
    event.competitionContext ||
    (event.category === "competition" ? "competitive" : "none"),
  recoveryCost: event.recoveryCost || "moderate",
  modifiable: event.modifiable || "yes",
  location: String(event.location || "").trim(),
  equipment: String(event.equipment || "").trim(),
  repeat: event.repeat || "none",
  weekdays: Array.isArray(event.weekdays) ? event.weekdays : [],
  reminderMinutes: Math.max(0, Number(event.reminderMinutes) || 30),
  description: String(event.description || ""),
  activityKey: event.activityKey || null,
});

export function loadUserCalendarEvents() {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(COMPANION_CALENDAR_KEY) || "[]",
    );
    return Array.isArray(parsed) ? parsed.map(normalizeProgramEvent) : [];
  } catch (_error) {
    return [];
  }
}

export function saveUserCalendarEvents(events) {
  const normalized = (events || []).map(normalizeProgramEvent);
  try {
    localStorage.setItem(COMPANION_CALENDAR_KEY, JSON.stringify(normalized));
    window.dispatchEvent(
      new CustomEvent("adaptive-calendar-updated", {
        detail: { eventCount: normalized.length, schemaVersion: 2 },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("apos-local-data-changed", {
        detail: { source: "calendar-programs", eventCount: normalized.length },
      }),
    );
  } catch (_error) {
    // The current session still uses the normalized events.
  }
  return normalized;
}

export function createUserCalendarEvent(values = {}) {
  return normalizeProgramEvent({ ...values, source: "user" });
}

export function assessmentCalendarEvents({ assessment, baselinePlan }) {
  const generated = (baselinePlan?.week?.sessions || [])
    .filter((session) => session.period !== "Scheduled activity")
    .map((session) =>
      normalizeProgramEvent({
        id: `assessment-${assessment?.completedAt || "baseline"}-${session.id}`,
        source: "assessment-plan",
        title: session.activity || session.period,
        date: session.date,
        time: session.time,
        durationMinutes: session.durationMinutes,
        category:
          session.period === "Bedtime support" ||
          session.period === "Morning preparation"
            ? "recovery"
            : "workout",
        modality:
          session.period === "Main physiological input" ? "mixed" : "mobility",
        usualEffort: String(session.intensity || "moderate")
          .toLowerCase()
          .includes("light")
          ? "easy"
          : "moderate",
        familiarity: "familiar",
        recoveryCost: "low",
        reminderMinutes: session.period === "Bedtime support" ? 10 : 30,
        description: [session.preparation, session.recovery]
          .filter(Boolean)
          .join(" "),
      }),
    );

  const recurring = (
    assessment?.answers?.schedule?.scheduledActivities || []
  ).map((item) =>
    normalizeProgramEvent({
      id: `assessment-recurring-${assessment?.completedAt || "baseline"}-${item.id}`,
      source: "assessment-scheduled",
      title: activityLabel(item.activity),
      date: localDateKey(new Date()),
      time: item.time || "18:00",
      durationMinutes: item.duration || 60,
      category: "sport",
      modality: "sport-practice",
      usualEffort: item.effort || "moderate",
      familiarity: "familiar",
      socialFormat: "team",
      repeat: "weekly",
      weekdays: item.days || [],
      reminderMinutes: 30,
      activityKey: item.activity,
      description: "Existing scheduled activity from an earlier assessment.",
    }),
  );

  return [...generated, ...recurring];
}

export function expandCalendarEvents(events, monthDate = new Date()) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const expanded = [];

  (events || []).map(normalizeProgramEvent).forEach((event) => {
    if (event.repeat !== "weekly") {
      const date = parseEventDate(event);
      if (date && date.getFullYear() === year && date.getMonth() === month) {
        expanded.push(event);
      }
      return;
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      if (!event.weekdays.includes(DAY_CODES[date.getDay()])) continue;
      expanded.push({
        ...event,
        id: `${event.id}-${localDateKey(date)}`,
        recurringId: event.id,
        date: localDateKey(date),
      });
    }
  });

  return expanded.sort((a, b) => {
    const first = parseEventDate(a)?.getTime() || 0;
    const second = parseEventDate(b)?.getTime() || 0;
    return first - second;
  });
}

export function upcomingCalendarEvents(events, now = new Date(), limit = 8) {
  return (events || [])
    .filter((event) => {
      const date = parseEventDate(event);
      return date && date.getTime() >= now.getTime() - 60 * 60 * 1000;
    })
    .sort((a, b) => parseEventDate(a) - parseEventDate(b))
    .slice(0, limit);
}

const relativeBandLabel = (band) =>
  ({
    1: "minimal relative demand",
    2: "low relative demand",
    3: "moderate relative demand",
    4: "vigorous relative demand",
    5: "high/performance relative demand",
  })[Number(band)] || "moderate relative demand";

export const nominalEventBand = (event = {}) => {
  const effort =
    ({
      easy: 2,
      moderate: 3,
      challenging: 4,
      "very-challenging": 5,
      variable: 4,
    })[event.usualEffort] || 3;
  const familiarityAdjustment =
    ({ new: 1, some: 0.5, familiar: 0, "highly-familiar": -0.25 })[
      event.familiarity
    ] || 0;
  const competitionAdjustment =
    ({ none: 0, practice: 0.25, competitive: 0.75, variable: 0.5 })[
      event.competitionContext
    ] || 0;
  const recoveryAdjustment =
    ({ low: -0.25, moderate: 0, high: 0.75, variable: 0.5 })[
      event.recoveryCost
    ] || 0;
  return Math.max(
    1,
    Math.min(
      5,
      Math.round(effort + familiarityAdjustment + competitionAdjustment + recoveryAdjustment),
    ),
  );
};

const preparationText = (event) => {
  const parts = ["Use a gradual activity-specific warm-up"];
  if (event.equipment) parts.push(`prepare the required equipment (${event.equipment})`);
  if (event.location) parts.push(`account for the ${event.location} setting`);
  parts.push("use familiar hydration and nourishment when needed");
  return `${parts.join(", ")}.`;
};

export function buildScheduledActivityGuidance({ event, plan, checkIn }) {
  if (!event) return null;
  const currentNervousSystemState =
    plan?.currentState?.system || "the current reported nervous-system state";
  const readiness = checkIn?.physicalReadiness || "not yet confirmed";
  const targetBand = Number(plan?.stateMatch?.finalBand) || 3;
  const eventBand = nominalEventBand(event);
  const canModify = event.modifiable !== "no";
  const highDemand = eventBand >= 4;
  const fixedDemand = !canModify || ["competition", "work-demand", "travel"].includes(event.category);

  if (readiness === "pause" && ["workout", "sport", "competition", "recreation", "group-class", "coaching"].includes(event.category)) {
    return {
      eventId: event.id,
      preserveActivity: false,
      title: `${event.title} should wait or use an approved modification`,
      relativeDemand: "No ordinary exercise dose",
      instruction:
        "The current physical-readiness response does not support the normal exercise dose. Use the approved non-exercise option, a qualified modification, or appropriate real-world guidance instead.",
      reason: `${currentNervousSystemState} remains relevant, but physical readiness is ${readiness} and overrides ordinary matching.`,
    };
  }

  if (["counselling", "work-demand", "caregiving", "travel"].includes(event.category)) {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: `Prepare around ${event.title}`,
      relativeDemand: relativeBandLabel(eventBand),
      instruction:
        "Keep the demand on the calendar. Use preparation, environment, nourishment, movement, connection, or recovery around it rather than treating it as an exercise prescription.",
      reason: `${currentNervousSystemState} is the present context. This scheduled demand contributes to total nervous-system load and affects what should be added before or afterward.`,
    };
  }

  if (["recovery", "physiotherapy"].includes(event.category)) {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: `Keep ${event.title}`,
      relativeDemand: relativeBandLabel(eventBand),
      instruction:
        event.category === "physiotherapy"
          ? "Follow the entered rehabilitation structure and any professional modification rules. Do not add intensity simply because the current nervous-system reaction is high."
          : "Keep the routine easy enough to support transition and recovery rather than turning it into another training demand.",
      reason: "This event supports capacity or recovery and remains distinct from the main output matched to the nervous-system state.",
    };
  }

  if (fixedDemand) {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: `Keep ${event.title}; adjust what happens around it`,
      relativeDemand: relativeBandLabel(eventBand),
      instruction: `${preparationText(event)} Avoid adding unnecessary extra conditioning, and complete an intentional taper and recovery period afterward.`,
      reason: `${currentNervousSystemState} is the current context. Because this demand is competitive, externally fixed, or not easily modified, APOS preserves it and adjusts preparation, additional load, and recovery rather than pretending every effort can be controlled precisely.`,
    };
  }

  if (targetBand < eventBand && canModify) {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: `Keep ${event.title} in a modified form`,
      relativeDemand: relativeBandLabel(targetBand),
      instruction: `${preparationText(event)} Reduce duration, density, mechanical load, interval frequency, or technical complexity until the session fits ${relativeBandLabel(targetBand)}. Finish with a gradual recovery transition.`,
      reason: `The scheduled session normally represents ${relativeBandLabel(eventBand)}, while the current nervous-system state, control, capacity, recovery resources, and nearby calendar demand support ${relativeBandLabel(targetBand)} today.`,
    };
  }

  return {
    eventId: event.id,
    preserveActivity: true,
    title: `Keep ${event.title} as planned`,
    relativeDemand: relativeBandLabel(eventBand),
    instruction: `${preparationText(event)} Use the familiar session structure, preserve technical control and stopping ability, and complete a gradual recovery transition.`,
    reason: `${currentNervousSystemState} and the current capacity ceilings do not require an additional reduction below this event's normal relative demand.`,
  };
}

const escapeIcs = (value) =>
  String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const icsDate = (event, addMinutes = 0) => {
  const date = parseEventDate(event);
  if (!date) return "";
  date.setMinutes(date.getMinutes() + addMinutes);
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
    date.getDate(),
  )}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
};

export function buildEventIcs(event, guidance) {
  const reminder = Math.max(0, Number(event.reminderMinutes) || 0);
  const context = [
    guidance?.instruction,
    guidance?.reason,
    event.description,
    event.location ? `Location: ${event.location}` : null,
    event.equipment ? `Equipment: ${event.equipment}` : null,
    `Social format: ${event.socialFormat}`,
    `Competition/evaluation: ${event.competitionContext}`,
    `Normal recovery cost: ${event.recoveryCost}`,
  ]
    .filter(Boolean)
    .join("\n\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//FORR Health//APOS Companion//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${escapeIcs(event.id)}@apos-companion`,
    `DTSTAMP:${icsDate({
      ...event,
      date: localDateKey(new Date()),
      time: new Date().toTimeString().slice(0, 5),
    })}`,
    `DTSTART:${icsDate(event)}`,
    `DTEND:${icsDate(event, event.durationMinutes)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(context)}`,
    "BEGIN:VALARM",
    `TRIGGER:-PT${reminder}M`,
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeIcs(`APOS Companion reminder: ${event.title}`)}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadEventIcs(event, guidance) {
  const blob = new Blob([buildEventIcs(event, guidance)], {
    type: "text/calendar;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "apos-event"}.ics`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export async function requestCalendarNotifications() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  return Notification.requestPermission();
}

export function scheduleOpenAppReminders(events) {
  if (!("Notification" in window) || Notification.permission !== "granted") {
    return () => {};
  }
  const now = Date.now();
  const timers = [];
  (events || []).forEach((event) => {
    const start = parseEventDate(event)?.getTime();
    if (!start) return;
    const notifyAt = start - (Number(event.reminderMinutes) || 0) * 60000;
    const delay = notifyAt - now;
    if (delay <= 0 || delay > 24 * 60 * 60 * 1000) return;
    timers.push(
      window.setTimeout(() => {
        new Notification(event.title, {
          body:
            "Open APOS Companion for guidance matched to your current nervous-system state, preparation, and recovery.",
          icon: "/adaptive-companion-192.png",
          tag: event.id,
        });
      }, delay),
    );
  });
  return () => timers.forEach((timer) => window.clearTimeout(timer));
}
